/**
 * 云端只读代码服务的客户端。
 *
 * 部署后 nginx 把 /api/code/ 反代到云电脑上的 code-service.mjs，
 * 页面里只用相对路径，所以在任何域名下都能用，不必改配置。
 */

// 部署到平台后默认直连内测域名上的代码服务；本地开发走 vite 代理
const DEFAULT_BASE = "https://nc.jhzzbgxt.cn/api/code";
const BASE_KEY = "jhzz_code_base";
const TOKEN_KEY = "jhzz_code_token";

/** 代码服务地址：默认走同源反代，也可以直接填 http://云电脑IP:6390 */
export function readCloudBase(): string {
  return (localStorage.getItem(BASE_KEY) || DEFAULT_BASE).replace(/\/+$/, "");
}

export function saveCloudBase(base: string) {
  const value = base.trim().replace(/\/+$/, "");
  if (!value || value === DEFAULT_BASE) localStorage.removeItem(BASE_KEY);
  else localStorage.setItem(BASE_KEY, value);
}

export type CloudEntry = { path: string; size?: number; mtime?: number };
export type CloudTree = { dir: string; dirs: string[]; files: CloudEntry[] };
export type CloudFile = { path: string; code: string; lines: number };
export type CloudHit = { path: string; line: number; text: string; score: number };

export function readCloudToken(): string {
  return sessionStorage.getItem(TOKEN_KEY) ?? "";
}

export function saveCloudToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearCloudToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = readCloudToken();
  const headers = new Headers(init?.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init?.body) headers.set("content-type", "application/json");

  let response: Response;
  try {
    response = await fetch(`${readCloudBase()}${path}`, { ...init, headers });
  } catch {
    throw new Error("连不上代码服务，请确认云电脑上的服务已启动、nginx 已反代 /api/code");
  }

  const raw = await response.text();
  let data: { message?: string } & T;
  try {
    data = (raw ? JSON.parse(raw) : {}) as { message?: string } & T;
  } catch {
    data = {} as { message?: string } & T;
  }
  if (!response.ok) {
    if (response.status === 401 && path !== "/login") clearCloudToken();
    throw new Error(data.message || `代码服务返回 ${response.status}`);
  }
  return data;
}

export async function cloudLogin(password: string): Promise<{ root: string }> {
  const data = await request<{ token: string; root: string }>("/login", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
  saveCloudToken(data.token);
  return { root: data.root };
}

export function cloudTree(dir = "."): Promise<CloudTree> {
  return request<CloudTree>(`/tree?dir=${encodeURIComponent(dir)}`);
}

export function cloudFile(path: string): Promise<CloudFile> {
  return request<CloudFile>(`/file?path=${encodeURIComponent(path)}`);
}

export async function cloudFind(query: string, hint = "", limit = 8): Promise<CloudHit[]> {
  const data = await request<{ hits: CloudHit[]; scanned: number }>(
    `/find?q=${encodeURIComponent(query)}&hint=${encodeURIComponent(hint)}&limit=${limit}`,
  );
  return data.hits;
}
